import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGroupMember } from '../../shared/hooks/useApi/useGroupMember';
import {
  AlertPosition,
  AlertType,
  DeviceType,
  GroupMemberContentMode,
} from '../../shared/enum/EnumRepository';
import { GroupMemberData } from '../../shared/dto/EntityRepository';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useGroupMemberContentModeStore } from '../../shared/store/groupMemberContentModeStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import MoleculeTextareaDescription from '../../shared/component/molecule/MoleculeTextareaDescription';
import { ContactInput } from '../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import {
  getContactsMessage,
  getMemberDescriptionPlaceholder,
} from '../../shared/constants/pageMessages';
import { MEMBER_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import orangeIcon from '../../asset/icon/orange.png';
import { imHere } from '../../shared/component/Animation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import GroupMemberProfileAndInfo from './GroupMemberProfileAndInfo';
import { useDeviceStore } from '../../shared/store/deviceStore';
import OrgInputDivVisi from '../../shared/component/organism/OrgInputDivVisi';
import OrgInputAnchorVisi from '../../shared/component/organism/OrgInputAnchorVisi';
import Arrow from '../../shared/component/Arrow';

type EditableSnsKey = 'instagram' | 'twitter' | 'facebook' | 'url1';
type EditablePropertyVisibleKey = 'email' | 'contact' | EditableSnsKey;

interface GroupMemberProps {
  member: GroupMemberData;
  onLinkedArchiveClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    handle: string,
  ) => void;
}

const GroupMember: React.FC<GroupMemberProps> = ({
  member,
  onLinkedArchiveClick,
}) => {
  const {
    updateGroupMemberPropertyVisible,
    deleteGroupMember,
  } = useGroupMember();
  const { isEditMode } = useEditMode();
  const updateGroupMemberLocal = useGroupMemberStore((s) => s.updateGroupMemberLocal);
  const groupMemberContentMode = useGroupMemberContentModeStore((s) => s.groupMemberContentMode);
  const deviceType = useDeviceStore((s) => s.deviceType);
  const { enqueueAlert } = useStandardAlertStore();

  const isEng = groupMemberContentMode === GroupMemberContentMode.ENG;
  const isMobile = deviceType === DeviceType.MOBILE;
  const showDescriptionBlock =
    isEditMode ||
    (isEng
      ? !!(member.descriptionEn ?? '').trim()
      : !!member.description);

  const handleChange = (updates: Partial<GroupMemberData>) => {
    updateGroupMemberLocal(member.id, updates);
  };

  const handleSnsChange = (key: EditableSnsKey, value: string) => {
    const trimmedPrefix = value.startsWith('@') ? value.slice(1) : value;
    handleChange({
      sns: {
        ...member.sns,
        [key]: trimmedPrefix,
      },
    });
  };

  const handlePropertyVisible = async (field: EditablePropertyVisibleKey) => {
    const propertyVisible = member.propertyVisible;
    await updateGroupMemberPropertyVisible({
      id: member.id,
      email: propertyVisible.email,
      contact: propertyVisible.contact,
      twitter: propertyVisible.twitter,
      instagram: propertyVisible.instagram,
      facebook: propertyVisible.facebook,
      youtube: propertyVisible.youtube,
      url1: propertyVisible.url1,
      [field]: !propertyVisible[field],
    });
  };

  const changePropertyVisibleMobile = (field: EditablePropertyVisibleKey) => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_CHANGE_VISIBILITY'),
      callBack: () => handlePropertyVisible(field),
    });
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_GROUP_MEMBER'),
      callBack: async () => {
        await deleteGroupMember(member.id);
      },
    });
  };

  return (
    <GroupMemberComp
      id={`group-member-${member.id}`}
      $isMobile={isMobile}
      data-has-changed={member.hasChanged ? 'true' : 'false'}
    >
      {isEditMode && member.hasChanged && (
        <ChangedIconWrap>
          <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
        </ChangedIconWrap>
      )}
      <ProfileSection>
        <GroupMemberProfileAndInfo member={member} />
      </ProfileSection>
      {showDescriptionBlock && (
        <MoleculeTextareaDescription
          value={
            isEng
              ? member.descriptionEn ?? ''
              : member.description
          }
          handleChange={(e) =>
            handleChange(
              isEng
                ? { descriptionEn: e.target.value }
                : { description: e.target.value },
            )
          }
          StyledTextarea={NormalSmallTextArea}
          StyledDescription={Description}
          placeholder={getMemberDescriptionPlaceholder()}
          maxLength={MEMBER_DESCRIPTION_MAX_LENGTH}
          maxRows={100}
        />
      )}

      <ContactSection>
        <OrgInputDivVisi
          value={member.email ?? ''}
          defaultValue={getContactsMessage('CONTACT_DEFAULT_EMAIL')}
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_EMAIL')}
          handleChange={(e) => handleChange({ email: e.target.value })}
          inputStyle={ContactInput}
          StyledDiv={ContactValue}
          DefaultDiv={ContactDefault}
          visible={member.propertyVisible.email}
          changeVisible={() => handlePropertyVisible('email')}
          changeVisibleMobile={() => changePropertyVisibleMobile('email')}
        />
        <OrgInputDivVisi
          value={member.contact ?? ''}
          defaultValue={getContactsMessage('CONTACT_DEFAULT_CONTACT')}
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_CONTACT')}
          handleChange={(e) => handleChange({ contact: e.target.value })}
          inputStyle={ContactInput}
          StyledDiv={ContactValue}
          DefaultDiv={ContactDefault}
          visible={member.propertyVisible.contact}
          changeVisible={() => handlePropertyVisible('contact')}
          changeVisibleMobile={() => changePropertyVisibleMobile('contact')}
        />
      </ContactSection>
      <ContactSection>
        {member.linkedMemberHandle && (
          <ArchitraveLinkContainer>
            <ArchitraveLink
              to={`/@${member.linkedMemberHandle}/`}
              onClick={(event) =>
                onLinkedArchiveClick(event, member.linkedMemberHandle)
              }
            >
              @{member.linkedMemberHandle}
              <Arrow />
              <ArchitraveIndicator>Architrave</ArchitraveIndicator>
            </ArchitraveLink>
          </ArchitraveLinkContainer>
        )}
        <OrgInputAnchorVisi
          value={member.sns.instagram ? `@${member.sns.instagram}` : ''}
          defaultValueKey="CONTACT_DEFAULT_INSTAGRAM"
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_INSTAGRAM')}
          handleChange={(e) => handleSnsChange('instagram', e.target.value)}
          inputStyle={ContactInput}
          StyledDiv={ContactAnchor}
          visible={member.propertyVisible.instagram}
          changeVisible={() => handlePropertyVisible('instagram')}
          changeVisibleMobile={() => changePropertyVisibleMobile('instagram')}
        />
        <OrgInputAnchorVisi
          value={member.sns.twitter}
          defaultValueKey="CONTACT_DEFAULT_TWITTER"
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_TWITTER')}
          handleChange={(e) => handleSnsChange('twitter', e.target.value)}
          inputStyle={ContactInput}
          StyledDiv={ContactAnchor}
          visible={member.propertyVisible.twitter}
          changeVisible={() => handlePropertyVisible('twitter')}
          changeVisibleMobile={() => changePropertyVisibleMobile('twitter')}
        />
        <OrgInputAnchorVisi
          value={member.sns.facebook}
          defaultValueKey="CONTACT_DEFAULT_FACEBOOK"
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_FACEBOOK')}
          handleChange={(e) => handleSnsChange('facebook', e.target.value)}
          inputStyle={ContactInput}
          StyledDiv={ContactAnchor}
          visible={member.propertyVisible.facebook}
          changeVisible={() => handlePropertyVisible('facebook')}
          changeVisibleMobile={() => changePropertyVisibleMobile('facebook')}
        />
        <OrgInputAnchorVisi
          value={member.sns.url1}
          defaultValueKey="CONTACT_DEFAULT_URL1"
          placeholder={getContactsMessage('CONTACT_PLACEHOLDER_URL1')}
          handleChange={(e) => handleSnsChange('url1', e.target.value)}
          inputStyle={ContactInput}
          StyledDiv={ContactAnchor}
          visible={member.propertyVisible.url1}
          changeVisible={() => handlePropertyVisible('url1')}
          changeVisibleMobile={() => changePropertyVisibleMobile('url1')}
        />
      </ContactSection>

      {isEditMode && (
        <BtnContainer>
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
        </BtnContainer>
      )}
    </GroupMemberComp>
  );
};

export default GroupMember;

const GroupMemberComp = styled.section<{ $isMobile: boolean }>`
  position: relative;
  width: ${({ $isMobile }) =>
    ($isMobile ? 'calc(100% - 20px)' : 'clamp(320px, 65vw, 650px)')};
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: ${({ $isMobile }) => ($isMobile ? '4px 0px 16px 0px;' : '4px 8px 16px 8px;')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  margin-left: ${({ $isMobile }) => ($isMobile ? '6px' : '10px;')};
  color: ${({ theme }) => theme.colors.color_Gray_02};
  scroll-margin-top: 24px;
`;

const ChangedIconWrap = styled.div`
  position: absolute;
  top: 0;
  right: -8px;
  display: flex;
  align-items: center;
  ${imHere}
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

const ProfileSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Description = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  line-height: 1.6;
  overflow-wrap: anywhere;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ContactSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContactValue = styled.div`
  width: 100%;
  min-height: 16px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const ContactDefault = styled.div`
  width: fit-content;
  min-height: 16px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  ${({ theme }) => theme.typography.Body_04};
`;

const ContactAnchor = styled.div`
  position: relative;
  width: fit-content;
  min-width: 100px;
  height: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(4px, 0.4vw, 8px);
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-decoration: underline;
  ${({ theme }) => theme.typography.Body_04};
  cursor: pointer;
`;

const ArchitraveLink = styled(Link)`
  position: relative;
  width: fit-content;
  min-width: 100px;
  height: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(4px, 0.4vw, 8px);
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-decoration: underline;
  ${({ theme }) => theme.typography.Body_04};
  cursor: pointer;
`;

const ArchitraveIndicator = styled.div`
  position: absolute;
  left: calc(100% + 0.2rem);
  bottom: 0;
  width: 0;
  opacity: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  white-space: nowrap;
  transition: all 0.5s ease, width 1s ease;
`;

const ArchitraveLinkContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: flex-end;

  &:hover {
    ${ArchitraveIndicator} {
      width: 100px;
      opacity: 1;
    }
  }
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vw;
`;
